import React, { useState, useEffect } from 'react';
import { Requirement } from '../../types/kyb';
import RequirementCard from './RequirementCard';

interface RequirementsDashboardProps {
  requirements: Requirement[];
  onRequirementAction?: (requirementId: number | string) => void;
}

const RequirementsDashboard: React.FC<RequirementsDashboardProps> = ({
  requirements,
  onRequirementAction
}) => {
  // Track newly added requirements for animation
  const [newRequirements, setNewRequirements] = useState<(string | number)[]>([]);
  const [prevRequirementCount, setPrevRequirementCount] = useState(requirements.length);

  // Group requirements by category
  const groupedRequirements = requirements.reduce((groups, item) => {
    const category = item.category;
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(item);
    return groups;
  }, {} as Record<string, Requirement[]>);

  // Detect new requirements
  useEffect(() => {
    if (requirements.length > prevRequirementCount) {
      // Find newly added requirements
      const currentIds = requirements.map(r => r.id);
      const newIds = currentIds.filter(id => 
        !newRequirements.includes(id) && 
        requirements.findIndex(r => r.id === id) >= prevRequirementCount
      );
      
      if (newIds.length > 0) {
        setNewRequirements(prev => [...prev, ...newIds]);
        
        // Remove from "new" status after animation time
        setTimeout(() => {
          setNewRequirements(prev => prev.filter(id => !newIds.includes(id)));
        }, 3000);
      }
    }
    
    setPrevRequirementCount(requirements.length);
  }, [requirements, prevRequirementCount, newRequirements]);

  // Calculate progress for each category
  const getCategoryProgress = (categoryRequirements: Requirement[]) => {
    const total = categoryRequirements.length;
    const completed = categoryRequirements.filter(req => req.status === 'completed').length;
    return `${completed} of ${total} completed`;
  };

  return (
    <div className="p-6 h-full overflow-y-auto bg-neutral-50 border-l border-neutral-200">
      <div className="mb-8">
        <div className="flex items-center mb-2">
          <div className="mr-3 w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-600 text-white rounded-lg flex items-center justify-center shadow-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-neutral-800">Requirements</h2>
        </div>
        <div className="text-sm text-neutral-500 pl-11">Complete all verification items to proceed</div>
      </div>

      {Object.entries(groupedRequirements).map(([category, categoryRequirements]) => (
        <div key={category} className="mb-8">
          <div className="flex justify-between items-center mb-4 px-1">
            <div className="flex items-center">
              <div className="w-1 h-5 bg-primary-500 rounded-r mr-2"></div>
              <div className="text-sm font-semibold text-neutral-800">{category}</div>
            </div>
            <div className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-1 rounded-md">
              {getCategoryProgress(categoryRequirements)}
            </div>
          </div>

          <div className="space-y-3">
            {categoryRequirements.map(requirement => (
              <RequirementCard
                key={requirement.id}
                requirement={requirement}
                isNew={newRequirements.includes(requirement.id)}
                onAction={onRequirementAction}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default RequirementsDashboard;